import React from 'react';
import { ButtonBase, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { sectionBandSx } from './SectionHeader';

type CollapsibleSectionProps = {
    title: string,
    count: number,
    /** Open/closed state is remembered in localStorage under this key */
    storageKey: string,
    children: React.ReactNode
};

let counter = 0;

function readStored(storageKey: string) {
    try {
        return window.localStorage.getItem(storageKey) !== 'closed';
    } catch (e) {
        return true;
    }
}

/** Section band that toggles an animated Collapse of its rows. */
export default function CollapsibleSection({
    title, count, storageKey, children
}: CollapsibleSectionProps) {
    const [open, setOpen] = React.useState(() => readStored(storageKey));
    const regionId = React.useRef(`collapsible-${(counter += 1)}`).current;

    const toggle = () => {
        setOpen(!open);
        try {
            window.localStorage.setItem(storageKey, open ? 'closed' : 'open');
        } catch (e) {
            // ignore
        }
    };

    return (
        <>
            <Typography component="h2" sx={{ ...sectionBandSx, p: 0 }}>
                <ButtonBase
                    onClick={toggle}
                    aria-expanded={open}
                    aria-controls={regionId}
                    sx={{
                        width: '100%',
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        font: 'inherit',
                        letterSpacing: 'inherit',
                        textTransform: 'inherit',
                        color: 'inherit'
                    }}
                >
                    <span>
                        {title}
                        <Typography
                            component="span"
                            sx={{
                                ml: 1, fontSize: 'inherit', fontWeight: 400, opacity: 0.7
                            }}
                        >
                            {count}
                        </Typography>
                    </span>
                    <ExpandMoreIcon
                        fontSize="small"
                        aria-hidden
                        sx={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 200ms ease',
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                        }}
                    />
                </ButtonBase>
            </Typography>
            <Collapse in={open} id={regionId}>
                {children}
            </Collapse>
        </>
    );
}
